import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { LogOut, Mail, Phone, Calendar, Trash2, Plus, Pencil, Eye, FileText, Globe, MessageSquare, Star, Users, Upload, Download } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [inquiries, setInquiries] = useState([]);
  const [visas, setVisas] = useState([]);
  const [tours, setTours] = useState([]);
  const [blogs, setBlogs] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [agents, setAgents] = useState([]);
  const [ratecards, setRatecards] = useState([]);
  const [stats, setStats] = useState({ inquiries: 0, visas: 0, tours: 0, blogs: 0 });
  const [activeDialog, setActiveDialog] = useState(null);
  const [editItem, setEditItem] = useState({});
  const [viewInquiry, setViewInquiry] = useState(null);
  const [uploadForm, setUploadForm] = useState({ title: '', description: '', category: 'Thailand DMC', file: null });

  const token = localStorage.getItem('admin_token');
  const config = { headers: { Authorization: `Bearer ${token}` } };

  useEffect(() => {
    if (!token) {
      navigate('/admin');
      return;
    }
    fetchAll();
  }, []);

  const fetchAll = async () => {
    try {
      const [i, v, t, b, ts, ag, rc] = await Promise.all([
        axios.get(`${API}/admin/inquiries`, config),
        axios.get(`${API}/visa-services`),
        axios.get(`${API}/tour-packages`),
        axios.get(`${API}/blog-posts`),
        axios.get(`${API}/testimonials`),
        axios.get(`${API}/admin/agents`, config),
        axios.get(`${API}/admin/ratecards`, config)
      ]);
      setInquiries(i.data);
      setVisas(v.data);
      setTours(t.data);
      setBlogs(b.data);
      setTestimonials(ts.data);
      setAgents(ag.data);
      setRatecards(rc.data);
      setStats({ inquiries: i.data.length, visas: v.data.length, tours: t.data.length, blogs: b.data.length });
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem('admin_token');
        navigate('/admin');
      }
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    navigate('/admin');
  };

  const openDialog = (type, item = {}) => {
    setActiveDialog(type);
    setEditItem(item);
  };

  const closeDialog = () => {
    setActiveDialog(null);
    setEditItem({});
  };

  const saveItem = async () => {
    const endpoints = {
      visa: 'visa-services',
      tour: 'tour-packages',
      blog: 'blog-posts',
      testimonial: 'testimonials'
    };
    const endpoint = endpoints[activeDialog];
    if (!endpoint) return;

    try {
      if (editItem.id) {
        await axios.put(`${API}/admin/${endpoint}/${editItem.id}`, editItem, config);
        toast.success('Updated successfully');
      } else {
        await axios.post(`${API}/admin/${endpoint}`, editItem, config);
        toast.success('Created successfully');
      }
      closeDialog();
      fetchAll();
    } catch (err) {
      toast.error('Failed to save');
    }
  };

  const deleteItem = async (type, id) => {
    if (!window.confirm('Are you sure?')) return;
    const endpoints = {
      visa: 'visa-services',
      tour: 'tour-packages',
      blog: 'blog-posts',
      testimonial: 'testimonials',
      inquiry: 'inquiries'
    };
    try {
      await axios.delete(`${API}/admin/${endpoints[type]}/${id}`, config);
      toast.success('Deleted');
      fetchAll();
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const updateInquiryStatus = async (id, status) => {
    try {
      await axios.put(`${API}/admin/inquiries/${id}`, { status }, config);
      toast.success('Status updated');
      fetchAll();
    } catch (err) {
      toast.error('Failed');
    }
  };

  // ---- B2B agents ----
  const updateAgentStatus = async (agentId, status) => {
    try {
      await axios.put(`${API}/admin/agents/${agentId}`, { status }, config);
      toast.success(status === 'approved' ? 'Agent approved — approval email sent' : `Status set to ${status}`);
      fetchAll();
    } catch (err) {
      toast.error('Failed to update agent');
    }
  };

  const deleteAgent = async (agentId) => {
    if (!window.confirm('Delete this agent permanently?')) return;
    try {
      await axios.delete(`${API}/admin/agents/${agentId}`, config);
      toast.success('Agent deleted');
      fetchAll();
    } catch (err) {
      toast.error('Failed');
    }
  };

  // ---- Rate cards ----
  const uploadRatecard = async () => {
    if (!uploadForm.title || !uploadForm.file) {
      toast.error('Title and PDF file are required');
      return;
    }
    const fd = new FormData();
    fd.append('title', uploadForm.title);
    fd.append('description', uploadForm.description);
    fd.append('category', uploadForm.category);
    fd.append('file', uploadForm.file);
    try {
      await axios.post(`${API}/admin/ratecards`, fd, {
        headers: { ...config.headers, 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Rate card uploaded');
      setUploadForm({ title: '', description: '', category: 'Thailand DMC', file: null });
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    }
  };

  const deleteRatecard = async (rcId) => {
    if (!window.confirm('Delete this rate card?')) return;
    try {
      await axios.delete(`${API}/admin/ratecards/${rcId}`, config);
      toast.success('Rate card deleted');
      fetchAll();
    } catch (err) {
      toast.error('Failed');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm">
        <div className="container mx-auto px-4 py-3 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="Varni Visa" className="h-10 w-auto object-contain" />
            <span className="text-gray-600 text-sm font-medium">Admin Panel</span>
          </div>
          <Button onClick={handleLogout} variant="outline">
            <LogOut size={16} className="mr-2" /> Logout
          </Button>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {[
            { label: 'Inquiries', value: stats.inquiries, icon: MessageSquare, color: 'from-[#2D8DB4] to-[#247a9a]' },
            { label: 'Visa Services', value: stats.visas, icon: Globe, color: 'from-[#F7945D] to-[#e67f3d]' },
            { label: 'Tour Packages', value: stats.tours, icon: Calendar, color: 'from-[#2D8DB4] to-[#F7945D]' },
            { label: 'Blog Posts', value: stats.blogs, icon: FileText, color: 'from-[#F7945D] to-[#2D8DB4]' }
          ].map((s, i) => {
            const Icon = s.icon;
            return (
              <Card key={i}>
                <CardContent className="p-6">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="text-sm text-gray-600">{s.label}</p>
                      <p className="text-3xl font-bold mt-1">{s.value}</p>
                    </div>
                    <div className={`w-12 h-12 bg-gradient-to-br ${s.color} rounded-lg flex items-center justify-center`}>
                      <Icon className="text-white" size={24} />
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        <Tabs defaultValue="inquiries">
          <TabsList className="mb-6 flex-wrap h-auto">
            <TabsTrigger value="inquiries">Inquiries</TabsTrigger>
            <TabsTrigger value="agents" data-testid="admin-tab-agents">B2B Agents</TabsTrigger>
            <TabsTrigger value="ratecards" data-testid="admin-tab-ratecards">Rate Cards</TabsTrigger>
            <TabsTrigger value="visas">Visa Services</TabsTrigger>
            <TabsTrigger value="tours">Tour Packages</TabsTrigger>
            <TabsTrigger value="blogs">Blog Posts</TabsTrigger>
            <TabsTrigger value="testimonials">Testimonials</TabsTrigger>
          </TabsList>

          {/* Inquiries */}
          <TabsContent value="inquiries">
            <Card>
              <CardHeader>
                <CardTitle>Customer Inquiries</CardTitle>
              </CardHeader>
              <CardContent>
                {inquiries.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">No inquiries yet</p>
                ) : (
                  <div className="space-y-3">
                    {inquiries.map((inq) => (
                      <Card key={inq.id} className="border-l-4 border-l-[#2D8DB4]">
                        <CardContent className="p-4">
                          <div className="flex justify-between items-start mb-2">
                            <div>
                              <h4 className="font-bold">{inq.name}</h4>
                              <p className="text-sm text-gray-600">{inq.subject}</p>
                            </div>
                            <Badge variant={inq.status === 'new' ? 'default' : 'secondary'}>
                              {inq.status}
                            </Badge>
                          </div>
                          <div className="flex gap-4 text-xs text-gray-500 mb-2">
                            <span className="flex items-center gap-1"><Mail size={12} />{inq.email}</span>
                            <span className="flex items-center gap-1"><Phone size={12} />{inq.phone}</span>
                            <span className="flex items-center gap-1"><Calendar size={12} />{new Date(inq.created_at).toLocaleDateString()}</span>
                          </div>
                          <div className="flex gap-2 mt-3">
                            <Button size="sm" variant="outline" onClick={() => setViewInquiry(inq)}>
                              <Eye size={14} className="mr-1" /> View
                            </Button>
                            <Select value={inq.status} onValueChange={(v) => updateInquiryStatus(inq.id, v)}>
                              <SelectTrigger className="w-32 h-9">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="new">New</SelectItem>
                                <SelectItem value="contacted">Contacted</SelectItem>
                                <SelectItem value="converted">Converted</SelectItem>
                                <SelectItem value="closed">Closed</SelectItem>
                              </SelectContent>
                            </Select>
                            <Button size="sm" variant="destructive" onClick={() => deleteItem('inquiry', inq.id)}>
                              <Trash2 size={14} />
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* B2B Agents */}
          <TabsContent value="agents">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2"><Users size={20} /> B2B Travel Agent Network</CardTitle>
              </CardHeader>
              <CardContent>
                {agents.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">No B2B agents registered yet.</p>
                ) : (
                  <div className="space-y-3">
                    {agents.map((a) => (
                      <Card key={a.id} className={`border-l-4 ${
                        a.status === 'approved' ? 'border-l-green-500' :
                        a.status === 'rejected' ? 'border-l-red-500' :
                        a.status === 'suspended' ? 'border-l-gray-500' :
                        'border-l-[#F7945D]'
                      }`} data-testid={`agent-row-${a.id}`}>
                        <CardContent className="p-4">
                          <div className="flex justify-between items-start mb-2 gap-2">
                            <div>
                              <h4 className="font-bold">{a.agency_name}</h4>
                              <p className="text-sm text-gray-600">{a.name} · {a.city || 'No city'}</p>
                            </div>
                            <Badge variant={a.status === 'approved' ? 'default' : 'secondary'}>{a.status}</Badge>
                          </div>
                          <div className="flex flex-wrap gap-4 text-xs text-gray-500 mb-3">
                            <span className="flex items-center gap-1"><Mail size={12} />{a.email}</span>
                            <span className="flex items-center gap-1"><Phone size={12} />{a.phone}</span>
                            {a.gst_iata && <span>GST/IATA: {a.gst_iata}</span>}
                            <span className="flex items-center gap-1"><Calendar size={12} />{new Date(a.created_at).toLocaleDateString()}</span>
                          </div>
                          <div className="flex gap-2 flex-wrap">
                            <Select value={a.status} onValueChange={(v) => updateAgentStatus(a.id, v)}>
                              <SelectTrigger className="w-32 h-9" data-testid={`agent-status-${a.id}`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="pending">Pending</SelectItem>
                                <SelectItem value="approved">Approve</SelectItem>
                                <SelectItem value="rejected">Reject</SelectItem>
                                <SelectItem value="suspended">Suspend</SelectItem>
                              </SelectContent>
                            </Select>
                            <Button size="sm" variant="destructive" onClick={() => deleteAgent(a.id)}>
                              <Trash2 size={14} />
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Rate Cards */}
          <TabsContent value="ratecards">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2"><FileText size={20} /> Rate Cards & Itinerary PDFs</CardTitle>
              </CardHeader>
              <CardContent>
                {/* Upload form */}
                <Card className="mb-6 bg-gray-50">
                  <CardContent className="p-4">
                    <h4 className="font-bold mb-3 flex items-center gap-2"><Upload size={16} /> Upload New Rate Card (PDF)</h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
                      <Input placeholder="Title (e.g., Schengen Rate Card 2025)" value={uploadForm.title} onChange={(e) => setUploadForm({ ...uploadForm, title: e.target.value })} data-testid="rc-upload-title" />
                      <Input placeholder="Category (Thailand DMC, Dubai, ...)" value={uploadForm.category} onChange={(e) => setUploadForm({ ...uploadForm, category: e.target.value })} />
                      <Input type="file" accept="application/pdf" onChange={(e) => setUploadForm({ ...uploadForm, file: e.target.files[0] })} data-testid="rc-upload-file" />
                    </div>
                    <Input placeholder="Short description" value={uploadForm.description} onChange={(e) => setUploadForm({ ...uploadForm, description: e.target.value })} className="mb-3" />
                    <Button onClick={uploadRatecard} className="bg-[#F7945D] hover:bg-[#e67f3d]" data-testid="rc-upload-submit">
                      <Upload size={16} className="mr-2" /> Upload PDF
                    </Button>
                  </CardContent>
                </Card>

                {ratecards.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">No rate cards uploaded yet.</p>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {ratecards.map((rc) => (
                      <Card key={rc.id} className="border" data-testid={`rc-row-${rc.id}`}>
                        <CardContent className="p-4 flex justify-between items-start gap-3">
                          <div className="flex gap-3 min-w-0">
                            <FileText size={24} className="text-[#2D8DB4] flex-shrink-0 mt-1" />
                            <div className="min-w-0">
                              <h4 className="font-bold truncate">{rc.title}</h4>
                              <p className="text-xs text-gray-500 truncate">{rc.filename}</p>
                              <div className="flex gap-2 mt-1">
                                <Badge variant="secondary" className="text-xs">{rc.category}</Badge>
                                {rc.file_size && <span className="text-xs text-gray-400">{(rc.file_size / 1024).toFixed(1)} KB</span>}
                              </div>
                            </div>
                          </div>
                          <Button size="sm" variant="destructive" onClick={() => deleteRatecard(rc.id)}>
                            <Trash2 size={14} />
                          </Button>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Visas */}
          <TabsContent value="visas">
            <Card>
              <CardHeader className="flex flex-row justify-between items-center">
                <CardTitle>Visa Services</CardTitle>
                <Button onClick={() => openDialog('visa', { country: '', type: 'Tourist Visa', processingTime: '', price: '', validity: '', icon: 'Globe', popular: false })} className="bg-[#2D8DB4]">
                  <Plus size={16} className="mr-1" /> Add Visa
                </Button>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {visas.map((v) => (
                    <Card key={v.id} className="border">
                      <CardContent className="p-4 flex justify-between items-center">
                        <div>
                          <h4 className="font-bold">{v.country} - {v.type}</h4>
                          <p className="text-sm text-gray-500">{v.price} | {v.processingTime}</p>
                        </div>
                        <div className="flex gap-2">
                          <Button size="sm" variant="outline" onClick={() => openDialog('visa', v)}>
                            <Pencil size={14} />
                          </Button>
                          <Button size="sm" variant="destructive" onClick={() => deleteItem('visa', v.id)}>
                            <Trash2 size={14} />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Tours */}
          <TabsContent value="tours">
            <Card>
              <CardHeader className="flex flex-row justify-between items-center">
                <CardTitle>Tour Packages</CardTitle>
                <Button onClick={() => openDialog('tour', { name: '', destination: '', duration: '', price: '', image: '', description: '', featured: false })} className="bg-[#F7945D]">
                  <Plus size={16} className="mr-1" /> Add Tour
                </Button>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {tours.map((t) => (
                    <Card key={t.id} className="border">
                      <CardContent className="p-4 flex justify-between items-center">
                        <div>
                          <h4 className="font-bold">{t.name}</h4>
                          <p className="text-sm text-gray-500">{t.destination} | {t.price}</p>
                        </div>
                        <div className="flex gap-2">
                          <Button size="sm" variant="outline" onClick={() => openDialog('tour', t)}>
                            <Pencil size={14} />
                          </Button>
                          <Button size="sm" variant="destructive" onClick={() => deleteItem('tour', t.id)}>
                            <Trash2 size={14} />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Blogs */}
          <TabsContent value="blogs">
            <Card>
              <CardHeader className="flex flex-row justify-between items-center">
                <CardTitle>Blog Posts</CardTitle>
                <Button onClick={() => openDialog('blog', { title: '', excerpt: '', content: '', image: '', author: 'Varni Visa Team', category: 'Travel Guide' })} className="bg-[#2D8DB4]">
                  <Plus size={16} className="mr-1" /> Add Post
                </Button>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {blogs.map((b) => (
                    <Card key={b.id} className="border">
                      <CardContent className="p-4 flex justify-between items-center">
                        <div>
                          <h4 className="font-bold">{b.title}</h4>
                          <p className="text-sm text-gray-500">{b.category} | {b.date || new Date(b.created_at || Date.now()).toLocaleDateString()}</p>
                        </div>
                        <div className="flex gap-2">
                          <Button size="sm" variant="outline" onClick={() => openDialog('blog', b)}>
                            <Pencil size={14} />
                          </Button>
                          <Button size="sm" variant="destructive" onClick={() => deleteItem('blog', b.id)}>
                            <Trash2 size={14} />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Testimonials */}
          <TabsContent value="testimonials">
            <Card>
              <CardHeader className="flex flex-row justify-between items-center">
                <CardTitle>Testimonials</CardTitle>
                <Button onClick={() => openDialog('testimonial', { name: '', location: '', rating: 5, text: '' })} className="bg-[#F7945D]">
                  <Plus size={16} className="mr-1" /> Add Testimonial
                </Button>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {testimonials.map((t) => (
                    <Card key={t.id} className="border">
                      <CardContent className="p-4">
                        <div className="flex justify-between items-start mb-2">
                          <div>
                            <h4 className="font-bold">{t.name}</h4>
                            <p className="text-sm text-gray-500">{t.location}</p>
                          </div>
                          <div className="flex gap-1">
                            {[...Array(t.rating)].map((_, i) => <Star key={i} size={14} className="fill-[#F7945D] text-[#F7945D]" />)}
                          </div>
                        </div>
                        <p className="text-sm text-gray-600 italic mb-3">"{t.text}"</p>
                        <div className="flex gap-2">
                          <Button size="sm" variant="outline" onClick={() => openDialog('testimonial', t)}>
                            <Pencil size={14} />
                          </Button>
                          <Button size="sm" variant="destructive" onClick={() => deleteItem('testimonial', t.id)}>
                            <Trash2 size={14} />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>

      {/* Edit Dialogs */}
      <Dialog open={activeDialog !== null} onOpenChange={(open) => !open && closeDialog()}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editItem.id ? 'Edit' : 'Add'} {activeDialog}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {activeDialog === 'visa' && (
              <>
                <Input placeholder="Country" value={editItem.country || ''} onChange={(e) => setEditItem({...editItem, country: e.target.value})} />
                <Input placeholder="Type (Tourist Visa)" value={editItem.type || ''} onChange={(e) => setEditItem({...editItem, type: e.target.value})} />
                <Input placeholder="Processing Time" value={editItem.processingTime || ''} onChange={(e) => setEditItem({...editItem, processingTime: e.target.value})} />
                <Input placeholder="Price (₹5,500)" value={editItem.price || ''} onChange={(e) => setEditItem({...editItem, price: e.target.value})} />
                <Input placeholder="Validity" value={editItem.validity || ''} onChange={(e) => setEditItem({...editItem, validity: e.target.value})} />
                <Input placeholder="Icon (Globe, Plane, etc.)" value={editItem.icon || ''} onChange={(e) => setEditItem({...editItem, icon: e.target.value})} />
                <label className="flex items-center gap-2"><input type="checkbox" checked={editItem.popular || false} onChange={(e) => setEditItem({...editItem, popular: e.target.checked})} /> Mark as Popular</label>
              </>
            )}
            {activeDialog === 'tour' && (
              <>
                <Input placeholder="Tour Name" value={editItem.name || ''} onChange={(e) => setEditItem({...editItem, name: e.target.value})} />
                <Input placeholder="Destination" value={editItem.destination || ''} onChange={(e) => setEditItem({...editItem, destination: e.target.value})} />
                <Input placeholder="Duration" value={editItem.duration || ''} onChange={(e) => setEditItem({...editItem, duration: e.target.value})} />
                <Input placeholder="Price" value={editItem.price || ''} onChange={(e) => setEditItem({...editItem, price: e.target.value})} />
                <Input placeholder="Image URL" value={editItem.image || ''} onChange={(e) => setEditItem({...editItem, image: e.target.value})} />
                <Textarea placeholder="Description" value={editItem.description || ''} onChange={(e) => setEditItem({...editItem, description: e.target.value})} />
                <label className="flex items-center gap-2"><input type="checkbox" checked={editItem.featured || false} onChange={(e) => setEditItem({...editItem, featured: e.target.checked})} /> Featured on Home Page</label>
              </>
            )}
            {activeDialog === 'blog' && (
              <>
                <Input placeholder="Title" value={editItem.title || ''} onChange={(e) => setEditItem({...editItem, title: e.target.value})} />
                <Input placeholder="Category" value={editItem.category || ''} onChange={(e) => setEditItem({...editItem, category: e.target.value})} />
                <Input placeholder="Image URL" value={editItem.image || ''} onChange={(e) => setEditItem({...editItem, image: e.target.value})} />
                <Input placeholder="Author" value={editItem.author || ''} onChange={(e) => setEditItem({...editItem, author: e.target.value})} />
                <Textarea placeholder="Excerpt (short summary)" value={editItem.excerpt || ''} onChange={(e) => setEditItem({...editItem, excerpt: e.target.value})} />
                <Textarea placeholder="Full content" rows={6} value={editItem.content || ''} onChange={(e) => setEditItem({...editItem, content: e.target.value})} />
              </>
            )}
            {activeDialog === 'testimonial' && (
              <>
                <Input placeholder="Customer Name" value={editItem.name || ''} onChange={(e) => setEditItem({...editItem, name: e.target.value})} />
                <Input placeholder="Location" value={editItem.location || ''} onChange={(e) => setEditItem({...editItem, location: e.target.value})} />
                <Select value={String(editItem.rating || 5)} onValueChange={(v) => setEditItem({...editItem, rating: parseInt(v)})}>
                  <SelectTrigger><SelectValue placeholder="Rating" /></SelectTrigger>
                  <SelectContent>
                    {[1,2,3,4,5].map(n => <SelectItem key={n} value={String(n)}>{n} Stars</SelectItem>)}
                  </SelectContent>
                </Select>
                <Textarea placeholder="Testimonial Text" rows={4} value={editItem.text || ''} onChange={(e) => setEditItem({...editItem, text: e.target.value})} />
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeDialog}>Cancel</Button>
            <Button onClick={saveItem} className="bg-[#2D8DB4]">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Inquiry Dialog */}
      <Dialog open={viewInquiry !== null} onOpenChange={(open) => !open && setViewInquiry(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Inquiry Details</DialogTitle>
          </DialogHeader>
          {viewInquiry && (
            <div className="space-y-3 text-sm">
              <div><strong>Name:</strong> {viewInquiry.name}</div>
              <div><strong>Email:</strong> {viewInquiry.email}</div>
              <div><strong>Phone:</strong> {viewInquiry.phone}</div>
              <div><strong>Type:</strong> {viewInquiry.inquiry_type}</div>
              <div><strong>Subject:</strong> {viewInquiry.subject}</div>
              <div><strong>Message:</strong><br /> {viewInquiry.message}</div>
              {viewInquiry.details && Object.keys(viewInquiry.details).length > 0 && (
                <div>
                  <strong>Additional Details:</strong>
                  <pre className="bg-gray-100 p-2 rounded mt-1 text-xs">{JSON.stringify(viewInquiry.details, null, 2)}</pre>
                </div>
              )}
              <div className="text-xs text-gray-500">Submitted: {new Date(viewInquiry.created_at).toLocaleString()}</div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;
